import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FavoritePageFiltersType } from 'pages/FavoritesPage';
import BailiffContext from 'utils/context/BailiffContext';
import { BailiffOfferResponseContent } from 'utils/state-managment/bailiff/bailiffOffer';
import { AllModules, AllModuleTypes } from 'utils/types/AllModules';
import { ModulesType } from 'utils/types/ModulesType';
import { UserReactions } from 'utils/types/OfferData';
import { ReactionType } from 'utils/types/ReactionType';

import {
    InteractiveElements
} from 'components/common/Card/common/V2InteractiveIconsWrapper/V2InteractiveIconsWrapper';
import { IconEnum } from 'components/common/Icon';
import { TabType } from 'components/common/Tabs/V2Tabs';
import { bailiffOfferListFunctions } from 'components/functions/offerListFunctions/bailiffOfferListFunctions/bailiffOfferListFunctions';
import { RenderList } from 'components/offerList/V2OfferList';

export const useBailiffHook = (filters: FavoritePageFiltersType, activeFilter: ModulesType) => {
    const {
        bailiffOfferState,
        deleteNote,
        updateNote,
        reactionUpdate,
        loadOfferDetailsAsync,
        loadNotesAsync,
        clearOffers,
        loadFavoriteOffers
    } = useContext(BailiffContext);
    const [renderList, setRenderList] = useState<RenderList[]>([]);

    const handleChangePage = useCallback((currentPage: number, rows = 25, sortType?: string) => {
        loadFavoriteOffers(currentPage ?? 1, rows ?? 25, filters, sortType);
    }, [filters, activeFilter]);

    const onNoteAdd = () => {
        // TODO: INVEST-257 Czy ta funkcja jest potrzebna?
        // if (!showNote && showLastNote) {
        //     showLastNote();
        // }
        //
        // if (!data.note?.noteId) {
        //     openOfferTab('note');
        // }
    };

    const onMessageClick = () => {
        onNoteAdd();
    };

    const handleDelete = (noteId: number, id: number) => {
        deleteNote(id, noteId);
    };

    const handleSaveNote = async (value: string, advertId: number, id?: number) => {
        updateNote(value, advertId, id || 0);
    };

    const toggleContainerDetails = (detailsOpen: boolean, offer: BailiffOfferResponseContent) => {
        if (!detailsOpen) {
            if (offer.reaction && !offer.reaction.viewedStatus) {
                reactionUpdate(offer.bailiffNoticeId, offer.location!, {
                    ...offer.reaction,
                    openedStatus: true,
                    viewedStatus: true
                } as ReactionType);
            }
            loadOfferDetailsAsync(offer.bailiffNoticeId);
        }
    };

    const handleChangeReaction = (name: keyof ReactionType, value: boolean, offer: BailiffOfferResponseContent) => {
        const userReaction = Object.assign({}, offer.reaction);

        if (name === 'likeStatus' && userReaction.unlikeStatus && value) {
            userReaction.unlikeStatus = false;
        } else if (name === 'unlikeStatus' && userReaction.likeStatus && value) {
            userReaction.likeStatus = false;
        }

        (userReaction[name] as boolean) = value;
        const city = offer.location;

        reactionUpdate(offer.bailiffNoticeId, city, userReaction as UserReactions);
    };

    const generateInteractiveElements = (offer: BailiffOfferResponseContent): InteractiveElements[] => {
        return [
            {
                tooltipText: 'Wyświetlono',
                icon: IconEnum.EYE,
                active: offer.reaction?.viewedStatus,
                disabled: offer.reaction?.viewedStatus === undefined,
                onClick: () => handleChangeReaction('viewedStatus', !offer.reaction?.viewedStatus, offer)
            },
            {
                tooltipText: 'Lubię to',
                icon: IconEnum.LIKE,
                active: offer.reaction?.likeStatus,
                onClick: () => handleChangeReaction('likeStatus', !offer.reaction?.likeStatus, offer)
            },
            {
                tooltipText: 'Nie lubię tego',
                icon: IconEnum.DISLIKE,
                active: offer.reaction?.unlikeStatus,
                disabled: offer.reaction?.unlikeStatus === undefined,
                onClick: () => handleChangeReaction('unlikeStatus', !offer.reaction?.unlikeStatus, offer)
            },
            {
                tooltipText: 'Notatki',
                icon: IconEnum.MESSAGE,
                active: offer.numberOfNotes ? offer.numberOfNotes > 0 : false,
                counter: offer.numberOfNotes ? offer.numberOfNotes || 1 : undefined,
                openTabOnClick: TabType.NOTES,
                onClick: onMessageClick
            },
            {
                tooltipText: 'Ogłoszenie nieaktualne',
                icon: IconEnum.BLOCKED,
                active: offer.reaction?.hideStatus,
                disabled: offer.reaction?.hideStatus === undefined,
                onClick: () => handleChangeReaction('hideStatus', !offer.reaction?.hideStatus, offer)
            },
            {
                tooltipText: 'Przeprowadzona rozmowa',
                icon: IconEnum.PHONE,
                active: offer.reaction?.phoneStatus,
                disabled: offer.reaction?.phoneStatus === undefined,
                onClick: () => handleChangeReaction('phoneStatus', !offer.reaction?.phoneStatus, offer)
            },
            {
                tooltipText: 'Umówione spotkanie',
                icon: IconEnum.PEOPLES_3,
                active: offer.reaction?.arrangedStatus,
                disabled: offer.reaction?.arrangedStatus === undefined,
                onClick: () => handleChangeReaction('arrangedStatus', !offer.reaction?.arrangedStatus, offer)
            },
            {
                tooltipText: 'Przejdź do ogłoszenia',
                icon: IconEnum.LINK,
                isLink: true,
                noText: true,
                text: <a target="_blank" rel="noopener noreferrer" href={offer.link}/>
            }
        ];
    };

    const handleNotes = async (offer: BailiffOfferResponseContent) => {
        loadNotesAsync(offer.bailiffNoticeId.toString(), AllModules.BAILIFF_NOTICE);
    };

    const generateCardData = (offer: BailiffOfferResponseContent) => {
        const subMainValues: React.ReactNode[] = [];

        return {
            preTitle: `Lokalizacja sądu: ${offer.officeLocation ? offer.officeLocation : 'brak informacji'}`,
            additionalInfoBadge: `${offer.dateAdded}`,
            infoElements: bailiffOfferListFunctions.generateInfoElements(offer),
            title: offer.location || '',
            interactiveElements: generateInteractiveElements(offer),
            // @ts-expect-error z Api dostajemy note na ulubionych zamiast newestNote
            note: offer.note,
            numberOfNotes: offer.numberOfNotes ? offer.numberOfNotes : undefined,
            getAllNotes: loadNotesAsync,
            offerId: offer.bailiffNoticeId.toString(),
            offerModule: offer.module as AllModuleTypes,
            thumbnailPath: offer.image ? offer.image : './defaultImg.png',
            subMainValues,
            adContext: { adId: offer.bailiffNoticeId, adModule: offer.module ?? '' }
        };
    };

    useEffect(() => {
        const offersList = bailiffOfferState.bailiffOfferList?.content
            ? bailiffOfferState.bailiffOfferList.content?.map((offer): RenderList => {
                const interactiveElements = generateInteractiveElements(offer);
                const detailsTabContent = bailiffOfferListFunctions.generateOfferDetailsTabContent(offer);

                return {
                    id: offer.bailiffNoticeId,
                    onSingleOfferClick: (_: React.MouseEvent, detailsOpen: boolean) => toggleContainerDetails(detailsOpen, offer),
                    baseCardContent: {
                        bigImage: true,
                        cardData: generateCardData(offer),
                        openOfferTab: () => {
                        }
                    },
                    detailsCardData: {
                        interactiveElements,
                        thumbnailPath: offer.image ? offer.image : './defaultImg.png',
                        additionalInfoBadge: `${offer.dateAdded}`,
                        onToggleContainerDetails: () => {
                        },
                        tabsContent: {
                            toggleContainerDetails: () => {
                            },
                            interactiveElements,
                            detailsTabContent: { ...detailsTabContent, adContext: { adId: offer.bailiffNoticeId, adModule: offer.module ?? '' } },
                            notesTabContent: {
                                notes: bailiffOfferState.notes,
                                handleDelete: (noteId: string) => handleDelete(Number(noteId), offer.bailiffNoticeId),
                                handleSaveNote: (value: string, id?: string) => handleSaveNote(value, offer.bailiffNoticeId, Number(id))
                            },
                            numberOfNotes: offer.numberOfNotes || 0,
                            handleLoadNotes: () => handleNotes(offer)
                        }
                    }
                };
            })
            : [];

        setRenderList(offersList);

        return () => {
            if (location.pathname !== '/favorites') {
                clearOffers();
            }
        };
    }, [
        bailiffOfferState.bailiffOfferList?.content,
        bailiffOfferState.notes,
        activeFilter
    ]);

    const handleFavoriteSearch = (page?: number, size?: number, sortType?: string) => {
        loadFavoriteOffers(page ?? 1, size ?? 25, filters, sortType);
    };

    return {
        bailiffOfferState,
        handleChangePage,
        handleFavoriteSearch,
        renderList
    };
};

